import { Box, HStack, Text } from "@chakra-ui/react";
import type { Ref } from "react";
import type { BudgetData } from "./BudgetSelector";
import { formatBudgetTwoDecimals } from "./guidedBudgetEditorMoney";
import {
  guidedBudgetFieldBorder,
  guidedBudgetFieldShadow,
} from "./guidedBudgetEditorTokens";
import { GuidedBudgetCurrencyInput } from "./GuidedBudgetCurrencyInput";

type GuidedBudgetAmountSummaryProps = {
  isScheduled: boolean;
  scheduledTotal: number;
  budget?: BudgetData;
  amountInputRef?: Ref<HTMLInputElement>;
  onChangeBudgetAmount: (amount: number | null) => void;
};

export function GuidedBudgetAmountSummary({
  isScheduled,
  scheduledTotal,
  budget,
  amountInputRef,
  onChangeBudgetAmount,
}: GuidedBudgetAmountSummaryProps) {
  const budgetAmountSummaryRow = (
    <HStack
      spacing="2px"
      justify="flex-end"
      align="baseline"
      w="full"
      lineHeight="23px"
      fontSize="md"
      letterSpacing="-0.48px"
      whiteSpace="nowrap"
    >
      <Text as="span" fontWeight="medium" color="rgba(34, 34, 34, 0.6)" flexShrink={0}>
        $
      </Text>
      {isScheduled ? (
        <Text
          as="span"
          fontWeight="normal"
          color="#222222"
          sx={{ fontVariantNumeric: "tabular-nums" }}
          minW={0}
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {formatBudgetTwoDecimals(scheduledTotal)}
        </Text>
      ) : (
        <GuidedBudgetCurrencyInput
          ref={amountInputRef}
          amount={budget?.amount?.amount}
          onCommit={onChangeBudgetAmount}
          variant="unstyled"
          type="text"
          inputMode="decimal"
          autoComplete="off"
          flex={1}
          minW={0}
          textAlign="right"
          border="none"
          boxShadow="none"
          outline="none"
          bg="transparent"
          p={0}
          h="23px"
          minH="23px"
          lineHeight="23px"
          fontSize="md"
          fontWeight="normal"
          color="#222222"
          letterSpacing="-0.48px"
          cursor="text"
          sx={{
            fontVariantNumeric: "tabular-nums",
            MozAppearance: "textfield",
            "&::-webkit-outer-spin-button": { WebkitAppearance: "none", margin: 0 },
            "&::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 },
          }}
          _focusVisible={{ boxShadow: "none" }}
          _hover={{ borderColor: "transparent" }}
        />
      )}
    </HStack>
  );

  return (
    <Box
      {...(isScheduled
        ? {
            flexShrink: 0,
            w: { base: "full", md: "105px" },
            minW: { md: "105px" },
            maxW: { md: "105px" },
            px: "12px",
            py: "10px",
            borderRadius: "8px",
            bg: "transparent",
            borderWidth: "0",
            boxShadow: "none",
          }
        : {
            flexShrink: 0,
            w: { base: "full", md: "100px" },
            minW: { md: "100px" },
            maxW: { md: "100px" },
            borderWidth: "1px",
            borderColor: guidedBudgetFieldBorder,
            borderRadius: "8px",
            bg: "white",
            boxShadow: guidedBudgetFieldShadow,
            px: "12px",
            py: "10px",
            _focusWithin: {
              borderColor: "blue.500",
              boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
            },
          })}
    >
      {budgetAmountSummaryRow}
    </Box>
  );
}
